import { normalizeReport } from "./revisorData";

/**
 * Dados do dashboard de uma análise do Revisor, a partir de
 * /revisor/reports/:id/details e da lista de relatórios (para comparar com a
 * análise anterior). Regras vindas dos dados reais:
 * - fornecedor: `total` = encontradas + faltantes; a cobertura é
 *   encontradas ÷ total. O campo `percentual` da API divide pelo número de XMLs
 *   e passa de 100% (ex.: 851,9%), então não é usado;
 * - empresas: hoje a API repete os mesmos números em todas (bug do backend),
 *   então os faltantes por estabelecimento só aparecem quando a soma fecha com
 *   o total da análise (`estMissingReady`). Os documentos por estabelecimento
 *   vêm de cada SPED (`report.spedFiles`);
 * - `totalValue` não tem unidade confirmada (reais ou centavos): só entra como
 *   participação relativa, que vale nos dois casos.
 */

export const BANDS = [
  // Tons suaves: a escala vai do verde ao coral sem vermelho saturado
  { key: "full", label: "Todos com XML", short: "100%", test: (p) => p >= 99.95, color: "#34c38f" },
  { key: "high", label: "90% a 99%", short: "90–99%", test: (p) => p >= 90, color: "#9adbbf" },
  { key: "mid", label: "50% a 89%", short: "50–89%", test: (p) => p >= 50, color: "#f2c27a" },
  { key: "low", label: "Abaixo de 50%", short: "< 50%", test: (p) => p > 0, color: "#f2a594" },
  { key: "none", label: "Nenhum XML", short: "0%", test: () => true, color: "#e8776c" },
];

export const bandOf = (pct) => BANDS.find((b) => b.test(pct));

const PERIOD_MONTHS = ["JANEIRO", "FEVEREIRO", "MARCO", "ABRIL", "MAIO", "JUNHO", "JULHO", "AGOSTO", "SETEMBRO", "OUTUBRO", "NOVEMBRO", "DEZEMBRO"];

// Mês de um SPED: pelo nome do arquivo, pelo campo `period` ou, sem eles, o início da análise
const spedMonth = (f, fallback) => {
  const name = String(f.filename || "");
  const m = name.match(/_(\d{4})(\d{2})\.\w+$/) || name.match(/-(\d{4})(\d{2})\d{2}-\d{8}-/);
  if (m) return `${m[1]}-${m[2]}`;
  const p = String(f.period || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().match(/([A-Z]+)\s+(\d{4})/);
  const idx = p ? PERIOD_MONTHS.indexOf(p[1]) : -1;
  return idx >= 0 ? `${p[2]}-${String(idx + 1).padStart(2, "0")}` : fallback;
};

const monthsBetween = (start, end) => {
  if (!start) return [];
  const out = [];
  let [y, m] = start.split("-").map(Number);
  const last = end || start;
  for (let i = 0; i < 240; i += 1) {
    const key = `${y}-${String(m).padStart(2, "0")}`;
    out.push(key);
    if (key >= last) break;
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out;
};

/**
 * Abrangência: quais SPEDs entraram na análise. A API não informa o cadastro de
 * estabelecimentos (`/certificates/list` dá 404), então os esperados são os CNPJs
 * vistos em qualquer análise do projeto, inclusive esta.
 */
export function buildScope(rep, allReports) {
  const digits = (v) => String(v || "").replace(/\D/g, "");
  const known = new Set(rep.files.map((f) => digits(f.cnpj)).filter(Boolean));
  allReports.map(normalizeReport)
    .filter((r) => !rep.projeto || !r.projeto || r.projeto === rep.projeto)
    .forEach((r) => r.files.forEach((f) => { if (digits(f.cnpj)) known.add(digits(f.cnpj)); }));

  const months = monthsBetween(rep.startKey, rep.endKey);
  const sent = new Map();
  rep.files.forEach((f) => {
    const month = spedMonth(f, rep.startKey);
    const key = `${digits(f.cnpj)}|${month}`;
    const prev = sent.get(key);
    const docs = (f.nfes || 0) + (f.ctes || 0);
    sent.set(key, { docs: (prev?.docs || 0) + docs, file: f.filename });
  });

  const establishments = [...known].sort().map((cnpj) => {
    const cells = months.map((month) => {
      const hit = sent.get(`${cnpj}|${month}`);
      return { month, sent: !!hit, docs: hit?.docs || 0, file: hit?.file || "" };
    });
    const sentCount = cells.filter((c) => c.sent).length;
    const docs = cells.reduce((sum, c) => sum + c.docs, 0);
    let state = "missing";
    if (sentCount === months.length) state = docs > 0 ? "sent" : "idle";
    else if (sentCount > 0) state = "partial";
    return { cnpj, cells, sentCount, docs, state };
  });

  const cells = establishments.flatMap((e) => e.cells.map((c) => ({ ...c, cnpj: e.cnpj })));
  const sentTotal = cells.filter((c) => c.sent).length;
  const types = [...new Set(rep.files.map((f) => f.spedType).filter(Boolean))];
  return {
    months,
    establishments,
    cells,
    expected: cells.length,
    sent: sentTotal,
    missing: cells.length - sentTotal,
    complete: cells.length > 0 && sentTotal === cells.length,
    types,
    idle: establishments.filter((e) => e.state === "idle"),
    absent: establishments.filter((e) => e.state === "missing" || e.state === "partial"),
  };
}

const SMALL_WORDS = new Set(["de", "da", "do", "das", "dos", "e", "em"]);
const UPPER = new Set(["LTDA", "SA", "S/A", "ME", "EPP", "EIRELI", "S.A.", "S.A", "CIA"]);
export const displayName = (name) => String(name || "Sem nome")
  .toLowerCase()
  .split(/\s+/)
  .map((w, i) => {
    const up = w.toUpperCase();
    if (UPPER.has(up)) return up;
    if (i > 0 && SMALL_WORDS.has(w)) return w;
    return w.charAt(0).toUpperCase() + w.slice(1);
  })
  .join(" ");

export function buildAnalise(report, details, allReports = []) {
  const rep = normalizeReport(report);
  const suppliers = (details?.suppliers || []).map((s) => {
    const encontradas = s.encontradas || 0;
    const faltantes = s.faltantes || 0;
    const total = encontradas + faltantes;
    const pct = total > 0 ? (encontradas / total) * 100 : 0;
    return {
      key: `${s.code || s.cnpj}-${s.cnpj}`,
      raw: s,
      name: s.name || "Sem nome",
      cnpj: s.cnpj || "",
      encontradas,
      faltantes,
      total,
      pct,
      value: s.totalValue || 0,
      band: bandOf(pct).key,
    };
  });

  // Totais da análise vêm do relatório (os mesmos do card na tela inicial);
  // as somas por fornecedor servem só às divisões internas
  const missing = suppliers.reduce((s, x) => s + x.faltantes, 0);
  const valueTotal = suppliers.reduce((s, x) => s + x.value, 0);

  // Concentração dos faltantes: quantos fornecedores somam 80%
  const byMissing = suppliers.filter((s) => s.faltantes > 0).sort((a, b) => b.faltantes - a.faltantes);
  let acc = 0;
  let pareto = 0;
  for (let i = 0; i < byMissing.length; i += 1) {
    acc += byMissing[i].faltantes;
    if (acc / Math.max(1, missing) >= 0.8) { pareto = i + 1; break; }
  }
  const topMissing = byMissing.slice(0, 10);
  const topShare = topMissing.reduce((s, x) => s + x.faltantes, 0) / Math.max(1, missing);

  // Mesmo grupo econômico = mesma raiz do CNPJ (8 primeiros dígitos)
  const groupsMap = new Map();
  suppliers.forEach((x) => {
    const digits = x.cnpj.replace(/\D/g, "");
    const root = digits.length === 14 ? digits.slice(0, 8) : x.key;
    if (!groupsMap.has(root)) groupsMap.set(root, { key: root, name: x.name, members: [], encontradas: 0, faltantes: 0, total: 0 });
    const g = groupsMap.get(root);
    g.members.push(x);
    g.encontradas += x.encontradas;
    g.faltantes += x.faltantes;
    g.total += x.total;
  });
  // Raiz do CNPJ da própria empresa (dos SPEDs): faltantes dela são transferências internas
  const ownRoots = new Set(rep.files.map((f) => String(f.cnpj || "").replace(/D/g, "").slice(0, 8)).filter(Boolean));
  const groups = [...groupsMap.values()]
    .map((g) => ({ ...g, own: ownRoots.has(g.key), pct: g.total > 0 ? (g.encontradas / g.total) * 100 : 0, lead: [...g.members].sort((p, q) => q.faltantes - p.faltantes)[0] }))
    .filter((g) => g.faltantes > 0)
    .sort((p, q) => q.faltantes - p.faltantes);
  const topGroups = groups.slice(0, 10);
  const topGroupShare = topGroups.reduce((sum, g) => sum + g.faltantes, 0) / Math.max(1, missing);

  // Faixas de cobertura
  const bands = BANDS.map((b) => {
    const list = suppliers.filter((s) => s.band === b.key);
    return { ...b, count: list.length, missing: list.reduce((s, x) => s + x.faltantes, 0) };
  });

  // Valor ligado a fornecedores com pendência (participação, sem unidade)
  const valuePending = suppliers.filter((s) => s.faltantes > 0).reduce((s, x) => s + x.value, 0);

  // Estabelecimentos: documentos de cada SPED
  const digits = (v) => String(v || "").replace(/\D/g, "");
  const companyMissing = new Map((details?.companies || []).map((c) => [digits(c.cnpj), c.faltantes || 0]));
  const establishments = rep.files
    .map((f) => {
      const cnpj = digits(f.cnpj);
      return { cnpj, nfes: f.nfes || 0, ctes: f.ctes || 0, docs: (f.nfes || 0) + (f.ctes || 0), file: f.filename, missing: companyMissing.get(cnpj) || 0 };
    })
    .sort((a, b) => a.cnpj.localeCompare(b.cnpj));
  // Faltantes por estabelecimento só valem se fecharem com o total da análise.
  // Enquanto a API repetir o total em cada empresa, a soma não fecha e a tela os omite.
  const estMissingReady = companyMissing.size > 0
    && new Set(establishments.map((e) => e.cnpj)).size === establishments.length
    && establishments.reduce((s, e) => s + e.missing, 0) === rep.faltantes;
  const estDocs = establishments.reduce((s, e) => s + e.docs, 0);
  const estRanked = [...establishments].sort((p, q) => q.docs - p.docs);
  const estNfes = establishments.reduce((s, e) => s + e.nfes, 0);
  const estCtes = establishments.reduce((s, e) => s + e.ctes, 0);
  const estIdle = establishments.filter((e) => e.docs === 0);

  // Histórico: cobertura de todas as análises, em ordem de período
  const history = allReports.map(normalizeReport)
    .filter((r) => r.startKey)
    .sort((p, q) => p.startKey.localeCompare(q.startKey))
    .map((r) => ({ key: r.startKey, id: r.id, pct: Math.round(r.pct * 10) / 10, faltantes: r.faltantes, current: r.id === rep.id }));

  // Análise anterior (mês imediatamente antes, ou a mais recente antes desta)
  const others = allReports.map(normalizeReport).filter((r) => r.id !== rep.id && r.startKey && r.startKey < rep.startKey);
  const previous = others.sort((a, b) => b.startKey.localeCompare(a.startKey))[0] || null;
  return {
    rep,
    suppliers,
    docs: rep.total,
    found: rep.encontradas,
    missing: rep.faltantes,
    supplierMissing: missing,
    pct: rep.pct,
    withMissing: byMissing.length,
    clean: suppliers.length - byMissing.length,
    pareto,
    topMissing,
    topShare,
    topGroups,
    topGroupShare,
    groupCount: groups.length,
    ownMissing: groups.filter((g) => g.own).reduce((sum, g) => sum + g.faltantes, 0),
    ownRoots,
    bands,
    valueShare: valueTotal > 0 ? (valuePending / valueTotal) * 100 : null,
    establishments,
    estDocs,
    estRanked,
    estNfes,
    estCtes,
    estIdle,
    estMissingReady,
    scope: buildScope(rep, allReports),
    history,
    previous,
    delta: previous ? rep.pct - previous.pct : null,
  };
}
